import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useWaProvider } from "../lib/waProvider";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { MessageCircle, Send, Users, Megaphone, Search, Sparkles, Bot } from "lucide-react";
import { AiModelPicker, DEFAULT_PICK } from "../components/AiModelPicker";

export default function CustomerMessages() {
  const { label: waLabel } = useWaProvider();
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [waReady, setWaReady] = useState(false);
  const [viaWa, setViaWa] = useState(false);
  const [quick, setQuick] = useState([]);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickDraft, setQuickDraft] = useState({ title: "", text: "" });
  const [branches, setBranches] = useState([]);
  const [defaultBranchId, setDefaultBranchId] = useState(null);
  const [aiPick, setAiPick] = useState(DEFAULT_PICK);
  const [aiBusy, setAiBusy] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [auto, setAuto] = useState(null);
  const loadAuto = () => api.get("/ai/autoreply").then((r) => setAuto(r.data)).catch(() => {});
  useEffect(() => { loadAuto(); }, []);
  const saveAuto = async (patch) => {
    try {
      const { data } = await api.put("/ai/autoreply", { ...auto, ...patch });
      setAuto(data); toast.success(data.enabled ? "Risposta automatica AI attiva" : "Risposta automatica AI disattivata");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const aiDraft = async () => {
    if (!selected) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/reply-draft", { ...aiPick, customer_id: selected.customer_id });
      setReply(data.draft ?? data.reply ?? "");
      if (data.confidence === "high") toast.success(`Bozza pronta (${data.topic}) — controlla e invia`);
      else toast.warning("Bozza generata ma l'AI non è sicura: verifica prima di inviare");
    } catch (err) { toast.error(formatApiError(err)); } finally { setAiBusy(false); }
  };

  const loadQuick = () => api.get("/quick-replies").then((r) => setQuick(r.data.items || [])).catch(() => {});
  useEffect(() => {
    loadQuick();
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
    api.get("/settings/app").then((r) => setDefaultBranchId(r.data?.default_branch_id || null)).catch(() => {});
  }, []);

  // Indirizzo della sede preferita del cliente (fallback: sede predefinita → prima sede)
  const branchAddress = () => {
    const b = branches.find((x) => x.id === selected?.branch_id)
      || branches.find((x) => x.id === defaultBranchId)
      || branches[0];
    return b ? [b.name, b.address].filter(Boolean).join(" — ") : "";
  };

  const applyQuick = (q) => {
    const first = (selected?.customer_name || selected?.name || "").split(" ")[0] || "";
    setReply(q.text.replace(/\{nome\}/g, first).replace(/\{indirizzo\}/g, branchAddress()).replace(/\s{2,}/g, " ").trim());
  };
  const saveQuick = async (items) => {
    try { const { data } = await api.put("/quick-replies", { items }); setQuick(data.items); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const [notifOpen, setNotifOpen] = useState(false);
  const [notif, setNotif] = useState({ title: "", body: "", target: "all", customer_id: "", image_url: "" });
  const [uploadingImg, setUploadingImg] = useState(false);
  const notifImgRef = useRef(null);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get("/customer-messages/threads");
    setThreads(data);
  };

  useEffect(() => { loadThreads(); }, []);

  // Deep-link dalla campanella: /messaggi?customer=<id>
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get("customer");
    if (!cid || !threads.length) return;
    const t = threads.find((x) => x.customer_id === cid);
    if (t) setSelected(t);
    // eslint-disable-next-line
  }, [threads, searchParams]);

  useEffect(() => {
    // Risposta via WhatsApp disponibile se Cloud API attiva oppure Spoki connesso con automazione admin_reply
    api.get("/customer-messages/wa-ready")
      .then(({ data }) => { setWaReady(!!data?.ready); setViaWa(!!data?.ready); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/customer-messages/${selected.customer_id}`).then((r) => setThread(r.data));
  }, [selected]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const send = async () => {
    if (!selected || !reply.trim()) return;
    setBusy(true);
    try {
      const { data: sent } = await api.post("/customer-messages/reply", {
        customer_id: selected.customer_id,
        body: reply.trim(),
        via_whatsapp: waReady && viaWa,
      });
      if (sent.channel === "whatsapp") {
        if (sent.spoki_state === "accepted") toast.success("Risposta inviata anche su WhatsApp");
        else toast.warning(`Salvata nel thread, WhatsApp non inviato (${sent.spoki_reason || sent.spoki_state})`);
      }
      setReply("");
      const [t, ts] = await Promise.all([
        api.get(`/customer-messages/${selected.customer_id}`),
        api.get("/customer-messages/threads"),
      ]);
      setThread(t.data);
      setThreads(ts.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const sendNotification = async () => {
    if (!notif.title.trim() || !notif.body.trim()) return toast.error("Titolo e testo obbligatori");
    const target = notif.target === "one" ? { customer_ids: [notif.customer_id] } : {};
    if (notif.target === "one" && !notif.customer_id) return toast.error("Scegli un cliente");
    if (!window.confirm(`Invio notifica${notif.target === "all" ? " a TUTTI i clienti attivi" : ""}?`)) return;
    try {
      const payload = { title: notif.title, body: notif.body, ...target };
      if (notif.image_url) payload.image_url = notif.image_url;
      const { data } = await api.post("/notifications/create", payload);
      toast.success(`Notifica inviata a ${data.created} clienti`);
      setNotifOpen(false);
      setNotif({ title: "", body: "", target: "all", customer_id: "", image_url: "" });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadNotifImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNotif({ ...notif, image_url: data.url });
      toast.success("Immagine caricata");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingImg(false); }
  };

  const filtered = threads.filter((t) =>
    !q || t.customer_name.toLowerCase().includes(q.toLowerCase()) ||
    (t.customer_card || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-4 animate-fade-up" data-testid="customer-messages-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red"><MessageCircle className="h-3 w-3" /> MESSAGGI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Chat clienti & News</h1>
          <p className="text-zinc-400 mt-1">Rispondi ai clienti dal portale o invia una news broadcast.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          <Button variant="outline" onClick={() => setAutoOpen(true)} data-testid="ai-autoreply-open" className={`h-11 border-zinc-700 ${auto?.enabled ? "text-emerald-400 border-emerald-500/50" : ""}`}>
            <Bot className="h-4 w-4 mr-2" /> Risposta automatica AI {auto?.enabled ? "· ON" : "· OFF"}
          </Button>
          <Button data-testid="notif-new-button" onClick={() => setNotifOpen(true)} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
            <Megaphone className="h-4 w-4 mr-2" /> Nuova news
          </Button>
        </div>
      </div>

      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800" data-testid="ai-autoreply-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bot className="h-5 w-5" /> Risposta automatica AI ai messaggi dei clienti</DialogTitle></DialogHeader>
          {auto && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-zinc-400">A ogni messaggio in arrivo (WhatsApp via {waLabel} o portale) l'AI prepara una bozza usando orari, sedi, premi, punti e coupon del cliente. Se è sicura della risposta (domande semplici: orari, punti, premi, coupon, sedi) la invia da sola; altrimenti la lascia come bozza per lo staff.</p>
              <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-emerald-500" checked={!!auto.enabled} onChange={(e) => saveAuto({ enabled: e.target.checked })} data-testid="ai-autoreply-enabled" /> <b>Attiva risposta automatica</b></label>
              <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4" checked={auto.only_high_confidence !== false} onChange={(e) => saveAuto({ only_high_confidence: e.target.checked })} data-testid="ai-autoreply-high" /> Invia da sola solo se l'AI è sicura (consigliato)</label>
              <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4" checked={auto.via_whatsapp !== false} onChange={(e) => saveAuto({ via_whatsapp: e.target.checked })} data-testid="ai-autoreply-wa" /> Invia anche su WhatsApp (automazione Spoki "Risposta admin"){!waReady && <span className="text-amber-400 text-xs"> — non configurata: solo portale</span>}</label>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-zinc-400">Modello:</span>
                <AiModelPicker value={{ provider: auto.provider, model: auto.model }} onChange={(p) => saveAuto(p)} testid="ai-autoreply-pick" />
                <select value={auto.tone || "amichevole"} onChange={(e) => saveAuto({ tone: e.target.value })} className="bg-zinc-950 border border-zinc-800 rounded-md h-8 text-xs px-2" data-testid="ai-autoreply-tone"><option value="amichevole">Tono amichevole</option><option value="formale">Tono formale</option></select>
              </div>
              <div className="text-xs text-zinc-500" data-testid="ai-autoreply-stats">Risposte automatiche inviate: <b className="text-zinc-200">{auto.sent_total || 0}</b> · bozze preparate: <b className="text-zinc-200">{auto.drafts_total || 0}</b></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[75vh]">
        {/* Thread list */}
        <div className="card-elev p-3 md:col-span-1 flex flex-col">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              data-testid="msg-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca cliente…"
              className="pl-9 bg-zinc-950 border-zinc-800 h-10"
            />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1">
            {filtered.length === 0 && <div className="text-sm text-zinc-500 text-center py-8">Nessuna conversazione</div>}
            {filtered.map((t) => (
              <button
                key={t.customer_id}
                onClick={() => setSelected(t)}
                data-testid={`msg-thread-${t.customer_id}`}
                className={`w-full text-left p-3 rounded-lg border ${
                  selected?.customer_id === t.customer_id
                    ? "border-[color:var(--paprika)] bg-red-500/10"
                    : "border-zinc-800 hover:bg-zinc-900/50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.customer_name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">{t.customer_card}</div>
                  </div>
                  {t.unread > 0 && (
                    <span className="pill pill-red text-[10px] font-mono">{t.unread}</span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1 truncate">
                  {t.last_direction === "shop_to_customer" ? "↩ " : ""}{t.last_message}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat panel */}
        <div className="card-elev p-4 md:col-span-2 flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500">
              Seleziona una conversazione a sinistra
            </div>
          ) : (
            <>
              <div className="border-b border-zinc-800 pb-3 mb-3">
                <div className="font-heading font-bold text-lg">{selected.customer_name}</div>
                <div className="text-xs text-zinc-500 font-mono">{selected.customer_card} · {selected.customer_phone}</div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="msg-thread-panel">
                {thread.length === 0 && <div className="text-sm text-zinc-500 text-center py-4">Nessun messaggio</div>}
                {thread.map((m) => {
                  const isShop = m.direction === "shop_to_customer";
                  return (
                    <div key={m.id} className={`flex ${isShop ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl p-3 text-sm ${
                        isShop ? "bg-[color:var(--paprika)] text-white" : "bg-zinc-900 text-zinc-100"
                      }`}>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                        {!isShop && m.ai_draft?.reply && (
                          <button type="button" onClick={() => setReply(m.ai_draft.reply)} data-testid={`msg-ai-draft-${m.id}`}
                            className="mt-2 w-full text-left text-[11px] rounded-lg border border-dashed border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-amber-200 hover:bg-amber-500/10">
                            <span className="font-semibold">✨ Bozza AI ({m.ai_draft.confidence === "high" ? "sicura" : "da verificare"})</span> — clicca per usarla<br />{m.ai_draft.reply}
                          </button>
                        )}
                        <div className="text-[10px] opacity-60 mt-1">
                          {isShop && m.ai_auto && <span className="mr-1" data-testid={`msg-ai-auto-${m.id}`} title="Risposta inviata automaticamente dall'AI">🤖 AI ·</span>}
                          {m.channel === "whatsapp" && !isShop && <span className="mr-1" title={`Ricevuto via WhatsApp (${waLabel})`} data-testid={`msg-channel-wa-${m.id}`}>💬 WhatsApp ·</span>}
                          {m.channel === "whatsapp" && isShop && (
                            <span className="mr-1" data-testid={`msg-wa-state-${m.id}`} title={m.spoki_reason || m.spoki_state || ""}>
                              {m.spoki_state === "accepted" ? "💬 WhatsApp ✓" : `💬 WhatsApp ✗ ${m.spoki_reason || m.spoki_state || ""}`} ·
                            </span>
                          )}
                          {new Date(m.created_at).toLocaleString("it-IT")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-zinc-800 pt-3 space-y-2">
                <div className="flex flex-wrap items-center gap-1" data-testid="quick-replies-bar">
                  {quick.map((q) => (
                    <button key={q.id} type="button" onClick={() => applyQuick(q)} title={q.text} data-testid={`quick-reply-${q.id}`}
                      className="text-[11px] px-2 py-1 rounded-full border border-zinc-800 text-zinc-300 hover:border-amber-500/60 hover:text-amber-200 transition">
                      ⚡ {q.title || q.text.slice(0, 20)}
                    </button>
                  ))}
                  <button type="button" onClick={() => setQuickOpen(true)} data-testid="quick-replies-manage" className="text-[11px] px-2 py-1 text-zinc-500 hover:text-zinc-300">⚙ Gestisci</button>
                  <span className="flex-1" />
                  <AiModelPicker value={aiPick} onChange={setAiPick} testid="msg-ai-pick" />
                  <button type="button" onClick={aiDraft} disabled={aiBusy} data-testid="msg-ai-draft-button"
                    className="text-[11px] px-2.5 py-1 rounded-full border border-amber-500/60 text-amber-200 hover:bg-amber-500/10 transition inline-flex items-center gap-1 disabled:opacity-50">
                    <Sparkles className={`h-3 w-3 ${aiBusy ? "animate-pulse" : ""}`} /> {aiBusy ? "Scrivo…" : "Bozza AI"}
                  </button>
                </div>
                <label className={`flex items-center gap-2 text-[11px] ${waReady ? "text-zinc-300" : "text-zinc-600"}`} data-testid="msg-via-wa-toggle" title={waReady ? "La risposta viene inviata anche su WhatsApp tramite Spoki" : "Configura l'automazione 'Risposta admin' nel pannello Spoki per abilitare"}>
                  <input type="checkbox" className="accent-emerald-500" checked={waReady && viaWa} disabled={!waReady} onChange={(e) => setViaWa(e.target.checked)} data-testid="msg-via-wa-checkbox" />
                  💬 Invia anche su WhatsApp ({waLabel}){!waReady && " — automazione 'Risposta admin' non configurata"}
                </label>
                <div className="flex gap-2">
                <Textarea
                  data-testid="msg-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={2}
                  placeholder="Scrivi la tua risposta…"
                  className="bg-zinc-950 border-zinc-800 resize-none"
                  onKeyDown={(e) => (e.key === "Enter" && !e.shiftKey) && (e.preventDefault(), send())}
                />
                <Button
                  data-testid="msg-send-button"
                  onClick={send}
                  disabled={busy || !reply.trim()}
                  className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] shrink-0"
                >
                  <Send className="h-4 w-4" />
                </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={notifOpen} onOpenChange={setNotifOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800" data-testid="notif-dialog">
          <DialogHeader><DialogTitle>Nuova news / notifica</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300 mb-1 inline-block text-xs">Destinatari</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="notif-target-all"
                  onClick={() => setNotif({ ...notif, target: "all" })}
                  className={`p-3 rounded-lg border flex items-center gap-2 ${notif.target === "all" ? "border-[color:var(--paprika)] bg-red-500/10" : "border-zinc-800 bg-zinc-950"}`}
                >
                  <Users className="h-4 w-4" /> Tutti attivi
                </button>
                <button
                  data-testid="notif-target-one"
                  onClick={() => setNotif({ ...notif, target: "one" })}
                  className={`p-3 rounded-lg border flex items-center gap-2 ${notif.target === "one" ? "border-[color:var(--paprika)] bg-red-500/10" : "border-zinc-800 bg-zinc-950"}`}
                >
                  <MessageCircle className="h-4 w-4" /> Un cliente
                </button>
              </div>
            </div>
            {notif.target === "one" && (
              <div>
                <Label className="text-zinc-300 mb-1 inline-block text-xs">ID cliente</Label>
                <Input
                  data-testid="notif-cust-id"
                  value={notif.customer_id}
                  onChange={(e) => setNotif({ ...notif, customer_id: e.target.value })}
                  placeholder="UUID cliente"
                  className="bg-zinc-950 border-zinc-800 font-mono text-xs"
                />
              </div>
            )}
            <div>
              <Label className="text-zinc-300 mb-1 inline-block text-xs">Titolo *</Label>
              <Input
                data-testid="notif-title"
                value={notif.title}
                onChange={(e) => setNotif({ ...notif, title: e.target.value })}
                placeholder="Es. Domani chiusura straordinaria"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block text-xs">Testo *</Label>
              <Textarea
                data-testid="notif-body"
                value={notif.body}
                onChange={(e) => setNotif({ ...notif, body: e.target.value })}
                rows={4}
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block text-xs">Immagine (PNG/JPG, opz.)</Label>
              <div className="flex gap-2 items-center">
                <input
                  ref={notifImgRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="notif-image-file"
                  onChange={(e) => uploadNotifImage(e.target.files?.[0])}
                />
                <Button
                  data-testid="notif-image-upload"
                  variant="outline"
                  size="sm"
                  onClick={() => notifImgRef.current?.click()}
                  disabled={uploadingImg}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                >
                  📎 {uploadingImg ? "Upload…" : (notif.image_url ? "Cambia" : "Allega")}
                </Button>
                {notif.image_url && (
                  <div className="flex items-center gap-1">
                    <img src={notif.image_url.startsWith("http") ? notif.image_url : `${process.env.REACT_APP_BACKEND_URL}${notif.image_url}`} alt="" className="h-10 w-10 rounded object-cover border border-zinc-800" />
                    <button data-testid="notif-image-remove" onClick={() => setNotif({ ...notif, image_url: "" })} className="text-zinc-500 hover:text-red-400 text-xs">Rimuovi</button>
                  </div>
                )}
              </div>
            </div>
            <Button data-testid="notif-submit" onClick={sendNotification} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              <Send className="h-4 w-4 mr-2" /> Invia news
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800" data-testid="quick-replies-dialog">
          <DialogHeader><DialogTitle>Risposte rapide</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500 -mt-2">Usa <code>{"{nome}"}</code> per il nome del cliente e <code>{"{indirizzo}"}</code> per l'indirizzo della sua sede preferita.</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {quick.map((q) => (
              <div key={q.id} className="flex items-start justify-between gap-2 bg-zinc-950/60 border border-zinc-800 rounded-lg px-3 py-2" data-testid={`quick-reply-row-${q.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 font-medium">{q.title}</div>
                  <div className="text-[11px] text-zinc-500 line-clamp-2">{q.text}</div>
                </div>
                <button type="button" onClick={() => saveQuick(quick.filter((x) => x.id !== q.id))} data-testid={`quick-reply-remove-${q.id}`} className="text-[11px] text-red-400 hover:text-red-300 shrink-0">✕</button>
              </div>
            ))}
            {quick.length === 0 && <div className="text-xs text-zinc-500">Nessuna risposta rapida.</div>}
          </div>
          <div className="space-y-2 border-t border-zinc-800 pt-3">
            <Input data-testid="quick-reply-title" value={quickDraft.title} onChange={(e) => setQuickDraft({ ...quickDraft, title: e.target.value })} placeholder="Titolo (es. Orari)" className="bg-zinc-950 border-zinc-800 h-9" />
            <Textarea data-testid="quick-reply-text" value={quickDraft.text} onChange={(e) => setQuickDraft({ ...quickDraft, text: e.target.value })} rows={3} placeholder="Testo della risposta… Ciao {nome}!" className="bg-zinc-950 border-zinc-800 resize-none" />
            <Button data-testid="quick-reply-add" disabled={!quickDraft.text.trim()} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
              onClick={async () => { await saveQuick([...quick, { id: Date.now().toString(36), title: quickDraft.title.trim() || quickDraft.text.trim().slice(0, 20), text: quickDraft.text.trim() }]); setQuickDraft({ title: "", text: "" }); toast.success("Risposta rapida aggiunta"); }}>
              Aggiungi risposta rapida
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
